import * as React from 'react'
import { inject, observer } from 'mobx-react'
import { message, Select } from 'antd'
import { debounce } from 'lodash'
import { ProductSearchFilter } from '@getgreenline/homi-shared'
import { ProductModels, productApi } from '@getgreenline/products'
import { CurrentCompanyStore } from '../../stores/CurrentCompanyStore'
import { filterOption, parseErrorMsg } from '../../utilities/helpers'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  onSelect: (product: ProductModels.IBaseProduct) => void
  onEmptySelect?: () => void
  value?: string
  placeholder?: string
  isInventoryProducts?: boolean
  isSellableProducts?: boolean
  clearable?: boolean
  optionFilterFunction?: (products: ProductModels.IBaseProduct[]) => ProductModels.IBaseProduct[]
  doesNotAllowBatchTrackedProducts?: boolean
  className?: string
  style?: React.CSSProperties
  size?: 'small' | 'large' | 'default'
}

interface State {
  isLoading: boolean
  products: ProductModels.INestedProduct[]
}

const debounceTime = 600

@inject('currentCompanyStore')
@observer
export class ProductAsyncSelect extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      isLoading: false,
      products: [],
    }
  }

  componentDidMount() {
    const { value } = this.props
    this.loadProducts(value)
  }

  async loadProducts(inputValue = '') {
    const company = this.props.currentCompanyStore!.company!
    const searchFilter = new ProductSearchFilter({
      searchQuery: inputValue,
      selectedSupplierId: undefined,
      selectedCategoryIds: [],
      offset: 0,
      limit: 15,
    })

    this.setState({ isLoading: true })

    try {
      const products = (await productApi.getProducts(company.id, searchFilter)).products
      this.setState({ isLoading: false, products })
    } catch (error) {
      message.error(parseErrorMsg(error) || 'There was an error getting products')
    }
  }

  onInputChange = debounce((inputValue) => {
    if (inputValue.length > 0) {
      this.loadProducts(inputValue)
    }
  }, debounceTime)

  getPreFilteredProducts = () => {
    const { products } = this.state
    const { currentCompanyStore, isInventoryProducts, isSellableProducts } = this.props

    const flattenedProducts = currentCompanyStore!.productStore!.flattenProducts(products)
    const sellableProducts = currentCompanyStore!.productStore!.getSellableProducts(products)

    if (isInventoryProducts) {
      return flattenedProducts.filter((p) => p.trackInventory && p.isActive)
    } else if (isSellableProducts) {
      return sellableProducts
    } else {
      return flattenedProducts
    }
  }

  handleOptionFilterLogic = (products: ProductModels.IBaseProduct[]) => {
    const { optionFilterFunction } = this.props
    if (optionFilterFunction) {
      return optionFilterFunction(products)
    } else {
      return products
    }
  }

  handleBatchTrackedFilter = (products: ProductModels.IBaseProduct[]) => {
    const { doesNotAllowBatchTrackedProducts } = this.props
    if (doesNotAllowBatchTrackedProducts) {
      return products.filter((product) => !product.isBatchTracked)
    } else {
      return products
    }
  }

  getOptions = (products: ProductModels.IBaseProduct[]) => {
    return products.map((product) => (
      <Select.Option key={product.id} value={product.id}>
        {product.name}
      </Select.Option>
    ))
  }

  render() {
    const {
      onSelect,
      onEmptySelect = () => undefined,
      value,
      clearable = false,
      placeholder = 'Search a product',
      className,
      style,
      size,
    } = this.props
    const { isLoading } = this.state
    const preFilterProducts = this.getPreFilteredProducts()
    const filteredProducts = this.handleOptionFilterLogic(preFilterProducts)
    const products = this.handleBatchTrackedFilter(filteredProducts)

    const found = products.find((p) => p.id === value)
    const currValue = found ? found.id : undefined

    return (
      <Select<string | null>
        showSearch
        className={className}
        size={size || 'default'}
        dropdownMatchSelectWidth={false}
        value={currValue}
        allowClear={clearable}
        onChange={(productId) => {
          if (productId) {
            onSelect(products.find((p) => p.id === productId)!)
          } else {
            onEmptySelect()
          }
        }}
        onSearch={(value) => {
          this.onInputChange(value)
        }}
        filterOption={filterOption}
        placeholder={placeholder}
        loading={isLoading}
        style={style}
      >
        {this.getOptions(products)}
      </Select>
    )
  }
}
